import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  animate,
  AnimatePresence,
  motion,
  useMotionValue,
  type Variants,
} from "motion/react";
import { flushSync } from "react-dom";
import type { Project } from "@portfolio/shared/project";
import {
  ProjectImage,
  type ProjectImageHandle,
} from "../../components/ui/ProjectImage";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import styles from "./ProjectDetailOverlay.module.css";

interface ProjectDetailOverlayProps {
  project: Project | null;
  onClose: () => void;
  suppressHint?: boolean;
}

const baseSlideLabels = [
  "OVERVIEW",
  "PROBLEM",
  "APPROACH",
  "SOLUTION",
  "RESULT",
] as const;

/*
 * 일반 모바일 + Galaxy Z Fold/Z Flip 가로 화면을 모바일 구조로 처리한다.
 * 예: Z Fold 5 개발자 도구 882 × 344
 */
const mobileDetailMediaQuery =
  "(max-width: 820px), " +
  "(max-width: 920px) and (max-height: 500px) and (orientation: landscape)";

const copyContainerVariants: Variants = {
  hidden: {
    transition: {
      staggerChildren: 0.035,
      staggerDirection: -1,
    },
  },
  visible: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.075,
    },
  },
};

const copyItemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 22,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.58,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export function ProjectDetailOverlay({
  project,
  onClose,
  suppressHint = false,
}: ProjectDetailOverlayProps) {
  const reduceMotion = useReducedMotion();

  const hasGallery =
    (project?.subImages?.length ?? 0) > 0;

  const currentSlideLabels = hasGallery
    ? [...baseSlideLabels, "GALLERY"]
    : baseSlideLabels;

  const slideCount = currentSlideLabels.length;

  /*
   * 모바일 화면 여부
   */
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(
        mobileDetailMediaQuery,
      ).matches,
  );

  const problemSlideIndex = 1;
  const approachSlideIndex = 2;
  const solutionSlideIndex = 3;
  const resultSlideIndex = 4;
  const gallerySlideIndex = 5;

  const [activeSlide, setActiveSlide] =
    useState(0);

  const [showScrollHint, setShowScrollHint] =
    useState(true);
  const [
    showGalleryOpenHint,
    setShowGalleryOpenHint,
  ] = useState(false);
  const shouldShowScrollHint = showScrollHint && !suppressHint;

  const trackX = useMotionValue(0);
  const galleryDragX = useMotionValue(0);

  const activeSlideRef = useRef(0);
  const viewportWidthRef = useRef(0);
  const isDraggingRef = useRef(false);

  const dismissGalleryHint =
    useCallback(() => {
      setShowGalleryOpenHint(false);
    }, []);

  const pointerGestureRef = useRef<{
    pointerId: number;
    startX: number;
    startTrackX: number;
    startedAt: number;
    isGallery: boolean;
    galleryCommitted: boolean;
  } | null>(null);

  const touchGestureRef = useRef<{
    identifier: number;
    startX: number;
    startTrackX: number;
    startedAt: number;
    isGallery: boolean;
    galleryCommitted: boolean;
  } | null>(null);

  const trackAnimationRef = useRef<
    ReturnType<typeof animate> | null
  >(null);

  const galleryDragAnimationRef = useRef<
    ReturnType<typeof animate> | null
  >(null);

  const overlayRef =
    useRef<HTMLDivElement>(null);

  const viewportRef =
    useRef<HTMLDivElement>(null);

  const trackRef =
    useRef<HTMLDivElement>(null);

  const galleryRef =
    useRef<ProjectImageHandle>(null);

  const closeButtonRef =
    useRef<HTMLButtonElement>(null);

  const wheelDistance = useRef(0);
  const wheelLocked = useRef(false);

  const wheelUnlockTimer =
    useRef<number | null>(null);

  const interactionReadyAt = useRef(0);

  const detailHistoryActive =
    useRef(false);

  const historyClosePending =
    useRef(false);

  const backgroundScrollY = useRef(0);

  /*
   * 화면 크기 변경 감지
   */
  useEffect(() => {
    const mediaQuery = window.matchMedia(
      mobileDetailMediaQuery,
    );

    const handleChange = () => {
      setIsMobile(mediaQuery.matches);
    };

    handleChange();

    mediaQuery.addEventListener(
      "change",
      handleChange,
    );

    return () => {
      mediaQuery.removeEventListener(
        "change",
        handleChange,
      );
    };
  }, []);

  /*
   * 모바일/데스크톱 전환 시 첫 페이지로 이동
   */
  useEffect(() => {
    activeSlideRef.current = 0;
    setActiveSlide(0);
    setShowScrollHint(true);

    pointerGestureRef.current = null;
    touchGestureRef.current = null;
    isDraggingRef.current = false;

    trackAnimationRef.current?.stop();
    galleryDragAnimationRef.current?.stop();
    trackX.set(0);
    galleryDragX.set(0);
  }, [isMobile, trackX]);

  const settleGalleryDrag = () => {
    galleryDragAnimationRef.current?.stop();

    if (reduceMotion) {
      galleryDragX.set(0);
      return;
    }

    galleryDragAnimationRef.current = animate(
      galleryDragX,
      0,
      {
        duration: 0.34,
        ease: [0.22, 1, 0.36, 1],
      },
    );
  };

  const updateGalleryDrag = (
    distance: number,
  ): -1 | 0 | 1 => {
    galleryDragX.set(
      Math.max(
        Math.min(distance, 72),
        -72,
      ),
    );
    return 0;
  };

  const settleTrack = (
    slideIndex: number,
  ) => {
    const destination =
      -slideIndex *
      viewportWidthRef.current;

    trackAnimationRef.current?.stop();

    if (reduceMotion) {
      trackX.set(destination);
      return;
    }

    trackAnimationRef.current = animate(
      trackX,
      destination,
      {
        duration: 0.52,
        ease: [0.22, 1, 0.36, 1],
      },
    );
  };

  const moveSlide = (
    direction: -1 | 1,
  ) => {
    const currentSlide =
      activeSlideRef.current;

    const nextSlide = Math.min(
      Math.max(
        currentSlide + direction,
        0,
      ),
      slideCount - 1,
    );

    if (nextSlide === currentSlide) {
      settleTrack(currentSlide);
      return;
    }

    activeSlideRef.current = nextSlide;
    setActiveSlide(nextSlide);
    setShowScrollHint(nextSlide === 0);

    settleTrack(nextSlide);
  };

  const finishClose = useCallback(() => {
    trackAnimationRef.current?.stop();
    galleryDragAnimationRef.current?.stop();
    galleryDragX.set(0);

    pointerGestureRef.current = null;
    touchGestureRef.current = null;
    isDraggingRef.current = false;

    wheelDistance.current = 0;
    wheelLocked.current = false;

    if (
      wheelUnlockTimer.current !== null
    ) {
      window.clearTimeout(
        wheelUnlockTimer.current,
      );

      wheelUnlockTimer.current = null;
    }

    onClose();
  }, [onClose]);

  const closeDetail = useCallback(() => {
    if (
      detailHistoryActive.current &&
      !historyClosePending.current
    ) {
      historyClosePending.current = true;
      window.history.back();
      return;
    }

    finishClose();
  }, [finishClose]);

  const completeSwipe = (
    distance: number,
    velocity = 0,
    fromGallery = false,
  ) => {
    const isIntentionalSwipe =
      Math.abs(distance) >= 40 ||
      (
        Math.abs(distance) >= 8 &&
        Math.abs(velocity) >= 280
      );

    if (!isIntentionalSwipe) {
      if (fromGallery) {
        settleGalleryDrag();
      }
      settleTrack(
        activeSlideRef.current,
      );
      return;
    }

    const direction: -1 | 1 =
      distance > 0 ? -1 : 1;

    /*
     * 갤러리 페이지에서는 바깥 슬라이드를 넘기기 전에
     * 현재 갤러리 이미지를 먼저 이동한다.
     *
     * 첫 이미지의 이전 방향 또는 마지막 이미지의 다음 방향에서는
     * false가 반환되어 기존 상세 페이지 이동이 이어진다.
     */
    if (
      activeSlideRef.current ===
        gallerySlideIndex &&
      galleryRef.current?.moveVisual(
        direction,
      )
    ) {
      galleryDragX.set(0);
      settleTrack(
        activeSlideRef.current,
      );
      return;
    }

    if (fromGallery) {
      settleGalleryDrag();
    }

    if (
      activeSlideRef.current ===
        gallerySlideIndex &&
      direction === -1
    ) {
      moveSlide(direction);
      return;
    }

    if (
      activeSlideRef.current ===
        gallerySlideIndex &&
      direction === 1
    ) {
      settleTrack(
        activeSlideRef.current,
      );
      return;
    }

    if (
      direction === -1 &&
      activeSlideRef.current === 0
    ) {
      setShowScrollHint(true);
      settleTrack(0);
      return;
    }

    if (
      direction === 1 &&
      activeSlideRef.current ===
        slideCount - 1
    ) {
      settleTrack(
        activeSlideRef.current,
      );
      return;
    }

    moveSlide(direction);
  };

  /*
   * 브라우저 뒤로가기로 상세 닫기
   */
  useEffect(() => {
    if (!project) {
      return;
    }

    if (!detailHistoryActive.current) {
      detailHistoryActive.current = true;
      historyClosePending.current = false;

      window.history.pushState(
        {
          ...window.history.state,
          portfolioProjectDetail:
            project.id,
        },
        "",
        window.location.href,
      );
    }

    const handlePopState = () => {
      if (
        document.documentElement.dataset
          .imageLightboxOpen === "true"
      ) {
        return;
      }

      if (
        !detailHistoryActive.current
      ) {
        return;
      }

      detailHistoryActive.current = false;
      historyClosePending.current = false;

      finishClose();
    };

    window.addEventListener(
      "popstate",
      handlePopState,
    );

    return () => {
      window.removeEventListener(
        "popstate",
        handlePopState,
      );
    };
  }, [finishClose, project]);

  /*
   * 상세 화면 열기와 닫기 처리
   */
  useEffect(() => {
    if (!project) {
      return;
    }

    activeSlideRef.current = 0;
    setActiveSlide(0);

    pointerGestureRef.current = null;
    touchGestureRef.current = null;
    isDraggingRef.current = false;

    trackAnimationRef.current?.stop();
    galleryDragAnimationRef.current?.stop();
    trackX.set(0);
    galleryDragX.set(0);

    galleryRef.current?.resetVisual();

    setShowScrollHint(true);
    setShowGalleryOpenHint(false);

    interactionReadyAt.current =
      window.performance.now() + 720;

    backgroundScrollY.current =
      window.scrollY;

    const previousFocus =
      document.activeElement instanceof
      HTMLElement
        ? document.activeElement
        : null;

    const previousOverflow =
      document.documentElement.style
        .overflow;

    const previousScrollRestoration =
      window.history.scrollRestoration;

    window.history.scrollRestoration =
      "manual";

    document.documentElement.style
      .overflow = "hidden";

    window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    const handleKeyDown = (
      event: KeyboardEvent,
    ) => {
      if (
        document.documentElement.dataset
          .imageLightboxOpen === "true"
      ) {
        return;
      }

      if (event.key === "Escape") {
        closeDetail();
        return;
      }

      if (event.key === "ArrowLeft") {
        moveSlide(-1);
        return;
      }

      if (event.key === "ArrowRight") {
        moveSlide(1);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = [
        ...(
          overlayRef.current
            ?.querySelectorAll<HTMLElement>(
              "button:not(:disabled)",
            ) ?? []
        ),
      ];

      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];

      const last =
        focusable[
          focusable.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last?.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first?.focus();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      document.documentElement.style
        .overflow = previousOverflow;

      window.history.scrollRestoration =
        previousScrollRestoration;

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );

      galleryDragAnimationRef.current?.stop();
      galleryDragX.set(0);

      if (
        wheelUnlockTimer.current !== null
      ) {
        window.clearTimeout(
          wheelUnlockTimer.current,
        );
      }

      pointerGestureRef.current = null;
      touchGestureRef.current = null;
      isDraggingRef.current = false;

      wheelDistance.current = 0;
      wheelLocked.current = false;

      previousFocus?.focus({
        preventScroll: true,
      });

      const restoreScrollPosition = () => {
        window.scrollTo({
          top: backgroundScrollY.current,
          behavior: "auto",
        });
      };

      window.requestAnimationFrame(
        restoreScrollPosition,
      );

      window.setTimeout(
        restoreScrollPosition,
        120,
      );
    };
  }, [
    closeDetail,
    project,
    trackX,
  ]);

  /*
   * 화면 너비와 마우스 휠 처리
   */
  useEffect(() => {
    const viewport =
      viewportRef.current;

    if (!project || !viewport) {
      return;
    }

    const updateViewportWidth = () => {
      const nextWidth =
        viewport.clientWidth;

      if (
        nextWidth ===
          viewportWidthRef.current ||
        isDraggingRef.current
      ) {
        return;
      }

      viewportWidthRef.current =
        nextWidth;

      trackAnimationRef.current?.stop();

      trackX.set(
        -activeSlideRef.current *
          nextWidth,
      );
    };

    const resizeObserver =
      new ResizeObserver(
        updateViewportWidth,
      );

    updateViewportWidth();
    resizeObserver.observe(viewport);

    const handleWheel = (
      event: WheelEvent,
    ) => {
      event.preventDefault();

      if (
        window.performance.now() <
        interactionReadyAt.current
      ) {
        return;
      }

      if (wheelLocked.current) {
        return;
      }

      const delta =
        Math.abs(event.deltaX) >
        Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;

      if (delta === 0) {
        return;
      }

      if (
        wheelDistance.current !== 0 &&
        Math.sign(
          wheelDistance.current,
        ) !== Math.sign(delta)
      ) {
        wheelDistance.current = 0;
      }

      wheelDistance.current += delta;

      if (
        Math.abs(
          wheelDistance.current,
        ) < 48
      ) {
        return;
      }

      const direction: -1 | 1 =
        wheelDistance.current > 0
          ? 1
          : -1;

      wheelDistance.current = 0;
      wheelLocked.current = true;

      if (
        !(
          direction === -1 &&
          activeSlideRef.current === 0
        )
      ) {
        setShowScrollHint(false);
        if (
          activeSlideRef.current ===
          gallerySlideIndex
        ) {
          dismissGalleryHint();
        }
      }

      /*
       * 갤러리 페이지에서는 기존 오버레이 휠 입력으로
       * 갤러리 이미지를 먼저 넘긴다.
       */
      const movedGallery =
        activeSlideRef.current ===
          gallerySlideIndex &&
        galleryRef.current?.moveVisual(
          direction,
        );

      if (!movedGallery) {
        moveSlide(direction);
      }

      wheelUnlockTimer.current =
        window.setTimeout(() => {
          wheelLocked.current = false;
          wheelUnlockTimer.current = null;
        }, 720);
    };

    viewport.addEventListener(
      "wheel",
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      resizeObserver.disconnect();

      viewport.removeEventListener(
        "wheel",
        handleWheel,
      );
    };
  }, [
    dismissGalleryHint,
    isMobile,
    project,
    trackX,
  ]);

  useEffect(() => {
    if (!project) {
      return;
    }

    if (
      activeSlide === 0
    ) {
      setShowScrollHint(true);
    }

    if (
      activeSlide === gallerySlideIndex
    ) {
      setShowGalleryOpenHint(true);
    } else {
      setShowGalleryOpenHint(false);
    }
  }, [
    activeSlide,
    project,
  ]);

  /*
   * 모바일 터치 슬라이드 처리
   */
  useEffect(() => {
    const track = trackRef.current;

    if (!project || !track) {
      return;
    }

   const handleTouchStart = (
  event: TouchEvent,
) => {
  const target =
    event.target instanceof Element
      ? event.target
      : null;

  /*
   * 갤러리 좌우 버튼에서 시작한 터치만 제외
   */
  if (
    target?.closest(
      "button, a, [role='button'], [data-project-gallery]",
    )
  ) {
    return;
  }

  const touch =
    event.changedTouches[0];

  if (
    !touch ||
    touchGestureRef.current
  ) {
    return;
  }

  isDraggingRef.current = true;

  trackAnimationRef.current?.stop();

  touchGestureRef.current = {
    identifier: touch.identifier,
    startX: touch.clientX,
    startTrackX: trackX.get(),
    startedAt:
      window.performance.now(),
    isGallery:
      activeSlideRef.current ===
      gallerySlideIndex,
    galleryCommitted: false,
  };
};
    const handleTouchMove = (
      event: TouchEvent,
    ) => {
      const gesture =
        touchGestureRef.current;

      if (!gesture) {
        return;
      }

      const touch = [
        ...event.changedTouches,
      ].find(
        ({ identifier }) =>
          identifier ===
          gesture.identifier,
      );

      if (!touch) {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      const distance =
        touch.clientX -
        gesture.startX;

      if (gesture.isGallery) {
        if (!gesture.galleryCommitted) {
          const committedDirection =
            updateGalleryDrag(distance);

          if (committedDirection !== 0) {
            flushSync(() => {
              galleryRef.current?.moveVisual(
                committedDirection,
              );
            });
            galleryDragX.set(0);
            gesture.galleryCommitted = true;
          }
        }
      } else {
        trackX.set(
          gesture.startTrackX +
            distance,
        );
      }

      if (
        Math.abs(distance) >= 8 &&
        !(
          activeSlideRef.current ===
            0 &&
          distance > 0
        )
      ) {
        setShowScrollHint(false);
        if (
          activeSlideRef.current ===
          gallerySlideIndex
        ) {
          dismissGalleryHint();
        }
      }
    };

    const finishTouch = (
      event: TouchEvent,
      cancelled: boolean,
    ) => {
      const gesture =
        touchGestureRef.current;

      if (!gesture) {
        return;
      }

      const touch = [
        ...event.changedTouches,
      ].find(
        ({ identifier }) =>
          identifier ===
          gesture.identifier,
      );

      touchGestureRef.current = null;
      isDraggingRef.current = false;

      if (gesture.galleryCommitted) {
        return;
      }

      if (!touch || cancelled) {
        if (gesture.isGallery) {
          settleGalleryDrag();
        } else {
          settleTrack(
            activeSlideRef.current,
          );
        }
        return;
      }

      const distance =
        touch.clientX -
        gesture.startX;

      const elapsed = Math.max(
        window.performance.now() -
          gesture.startedAt,
        1,
      );

      const velocity =
        (distance / elapsed) * 1000;

      completeSwipe(
        distance,
        velocity,
        gesture.isGallery,
      );
    };

    const handleTouchEnd = (
      event: TouchEvent,
    ) => {
      finishTouch(event, false);
    };

    const handleTouchCancel = (
      event: TouchEvent,
    ) => {
      finishTouch(event, true);
    };

    track.addEventListener(
      "touchstart",
      handleTouchStart,
      {
        passive: true,
      },
    );

    track.addEventListener(
      "touchmove",
      handleTouchMove,
      {
        passive: false,
      },
    );

    track.addEventListener(
      "touchend",
      handleTouchEnd,
    );

    track.addEventListener(
      "touchcancel",
      handleTouchCancel,
    );

    return () => {
      track.removeEventListener(
        "touchstart",
        handleTouchStart,
      );

      track.removeEventListener(
        "touchmove",
        handleTouchMove,
      );

      track.removeEventListener(
        "touchend",
        handleTouchEnd,
      );

      track.removeEventListener(
        "touchcancel",
        handleTouchCancel,
      );
    };
  }, [
    isMobile,
    project,
    trackX,
  ]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          ref={overlayRef}
          className={styles.overlay}
          role="dialog"
          aria-modal="true"
          aria-label={`${project.title} 프로젝트 상세`}
          initial={
            reduceMotion
              ? false
              : {
                  x: "100%",
                }
          }
          animate={{
            x: 0,
          }}
          exit={
            reduceMotion
              ? {
                  opacity: 0,
                }
              : {
                  x: "100%",
                }
          }
          transition={{
            duration: reduceMotion
              ? 0.01
              : 0.72,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
        >
          <header className={styles.header}>
            <div>
              <span>
                {project.index}
                {" / PROJECT STORY"}
              </span>

              <strong>
                {project.title}
              </strong>
            </div>

            <button
              ref={closeButtonRef}
              type="button"
              className="detail-close-button"
              onClick={closeDetail}
              aria-label="프로젝트 상세 닫기"
            >
              <span aria-hidden="true">
                ×
              </span>
            </button>
          </header>

          <div
            ref={viewportRef}
            className={styles.viewport}
          >
            <div
              className={styles.dragLayer}
            >
              <motion.div
                ref={trackRef}
                className={styles.track}
                style={{
                  x: trackX,
                }}
                onPointerDown={(event) => {
  if (
    !event.isPrimary ||
    event.button !== 0 ||
    event.pointerType === "touch"
  ) {
    return;
  }

  const target =
    event.target instanceof Element
      ? event.target
      : null;

  /*
   * 버튼과 링크만 바깥 드래그에서 제외
   */
  if (
    target?.closest(
      `
        a,
        button,
        input,
        textarea,
        select,
        summary,
        [role="button"]
      `,
    ) !== null
  ) {
    return;
  }

  isDraggingRef.current = true;

  trackAnimationRef.current?.stop();

  pointerGestureRef.current = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startTrackX: trackX.get(),
    startedAt:
      window.performance.now(),
    isGallery:
      activeSlideRef.current ===
      gallerySlideIndex,
    galleryCommitted: false,
  };

  event.currentTarget.setPointerCapture(
    event.pointerId,
  );
}}
                onPointerMove={(event) => {
                  if (
                    event.pointerType ===
                    "touch"
                  ) {
                    return;
                  }

                  const gesture =
                    pointerGestureRef.current;

                  if (
                    !gesture ||
                    gesture.pointerId !==
                      event.pointerId
                  ) {
                    return;
                  }

                  const distance =
                    event.clientX -
                    gesture.startX;

                  if (gesture.isGallery) {
                    if (
                      !gesture.galleryCommitted
                    ) {
                      const committedDirection =
                        updateGalleryDrag(
                          distance,
                        );

                      if (
                        committedDirection !==
                        0
                      ) {
                        flushSync(() => {
                          galleryRef.current
                            ?.moveVisual(
                              committedDirection,
                            );
                        });
                        galleryDragX.set(0);
                        gesture.galleryCommitted =
                          true;
                      }
                    }
                  } else {
                    trackX.set(
                      gesture.startTrackX +
                        distance,
                    );
                  }

                  if (
                    Math.abs(distance) >=
                      8 &&
                    !(
                      activeSlideRef.current ===
                        0 &&
                      distance > 0
                    )
                  ) {
                    setShowScrollHint(false);
                    if (
                      activeSlideRef.current ===
                      gallerySlideIndex
                    ) {
                      dismissGalleryHint();
                    }
                  }
                }}
                onPointerUp={(event) => {
                  if (
                    event.pointerType ===
                    "touch"
                  ) {
                    return;
                  }

                  const gesture =
                    pointerGestureRef.current;

                  if (
                    !gesture ||
                    gesture.pointerId !==
                      event.pointerId
                  ) {
                    return;
                  }

                  const distance =
                    event.clientX -
                    gesture.startX;

                  const elapsed = Math.max(
                    window.performance.now() -
                      gesture.startedAt,
                    1,
                  );

                  const velocity =
                    (distance / elapsed) *
                    1000;

                  if (
                    event.currentTarget
                      .hasPointerCapture(
                        event.pointerId,
                      )
                  ) {
                    event.currentTarget
                      .releasePointerCapture(
                        event.pointerId,
                      );
                  }

                  pointerGestureRef.current =
                    null;

                  isDraggingRef.current =
                    false;

                  if (
                    gesture.galleryCommitted
                  ) {
                    return;
                  }

                  completeSwipe(
                    distance,
                    velocity,
                    gesture.isGallery,
                  );
                }}
                onPointerCancel={(
                  event,
                ) => {
                  if (
                    event.pointerType ===
                    "touch"
                  ) {
                    return;
                  }

                  const gesture =
                    pointerGestureRef.current;

                  if (
                    event.currentTarget
                      .hasPointerCapture(
                        event.pointerId,
                      )
                  ) {
                    event.currentTarget
                      .releasePointerCapture(
                        event.pointerId,
                      );
                  }

                  pointerGestureRef.current =
                    null;

                  isDraggingRef.current =
                    false;

                  if (
                    gesture
                      ?.galleryCommitted
                  ) {
                    return;
                  }

                  if (
                    activeSlideRef.current ===
                    gallerySlideIndex
                  ) {
                    settleGalleryDrag();
                  } else {
                    settleTrack(
                      activeSlideRef.current,
                    );
                  }
                }}
              >
                {/* OVERVIEW */}
                <section
                  className={`
                    ${styles.slide}
                    ${styles.overviewSlide}
                  `}
                  aria-hidden={
                    activeSlide !== 0
                  }
                >
                  <motion.div
                    className={
                      styles.slideCopy
                    }
                    initial={false}
                    variants={
                      reduceMotion
                        ? undefined
                        : copyContainerVariants
                    }
                    animate={
                      reduceMotion
                        ? undefined
                        : activeSlide === 0
                          ? "visible"
                          : "hidden"
                    }
                  >
                    <motion.span variants={copyItemVariants}>
                      01 / OVERVIEW
                    </motion.span>

                    <motion.h2 variants={copyItemVariants}>
                      {project.subtitle}
                    </motion.h2>

                    <motion.p variants={copyItemVariants}>
                      {project.overview}
                    </motion.p>

                    <motion.dl
                      variants={copyItemVariants}
                      className={
                        styles.projectMeta
                      }
                    >
                      <div>
                        <dt>기간</dt>

                        <dd>
                          {project.period}
                        </dd>
                      </div>

                      <div>
                        <dt>역할</dt>

                        <dd>
                          {
                            project.contribution
                          }
                        </dd>
                      </div>

                      <div>
                        <dt>기술</dt>

                        <dd>
                          {project.technologies.join(
                            " · ",
                          )}
                        </dd>
                      </div>

                      {project.link && (
                        <div>
                          <dt>사이트</dt>

                          <dd>
                            <a
                              className={
                                styles.projectSiteLink
                              }
                              href={
                                project.link
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              {project.link
                                .replace(
                                  /^https?:\/\//,
                                  "",
                                )
                                .replace(
                                  /\/$/,
                                  "",
                                )}

                              <span
                                aria-hidden="true"
                              >
                                ↗
                              </span>
                            </a>
                          </dd>
                        </div>
                      )}
                    </motion.dl>
                  </motion.div>

                </section>

                {/* PROBLEM */}
                <section
                  className={styles.slide}
                  aria-hidden={
                    activeSlide !==
                    problemSlideIndex
                  }
                >
                  <motion.div
                    className={
                      styles.readingColumn
                    }
                    initial={false}
                    variants={
                      reduceMotion
                        ? undefined
                        : copyContainerVariants
                    }
                    animate={
                      reduceMotion
                        ? undefined
                        : activeSlide === problemSlideIndex
                          ? "visible"
                          : "hidden"
                    }
                  >
                    <motion.span variants={copyItemVariants}>
                     02 / PROBLEM
                    </motion.span>

                    <motion.h2 variants={copyItemVariants}>
                      해결해야 할 핵심 과제
                    </motion.h2>

                    <motion.p variants={copyItemVariants}>
                      {project.problem}
                    </motion.p>
                  </motion.div>
                </section>

                {/* APPROACH */}
                <section
                  className={styles.slide}
                  aria-hidden={
                    activeSlide !==
                    approachSlideIndex
                  }
                >
                  <motion.div
                    className={
                      styles.readingColumn
                    }
                    initial={false}
                    variants={
                      reduceMotion
                        ? undefined
                        : copyContainerVariants
                    }
                    animate={
                      reduceMotion
                        ? undefined
                        : activeSlide === approachSlideIndex
                          ? "visible"
                          : "hidden"
                    }
                  >
                    <motion.span variants={copyItemVariants}>
                    03 / APPROACH
                    </motion.span>
                     
                    <motion.h2 variants={copyItemVariants}>
                    직접 담당한 핵심 기능
                    </motion.h2>

                    <motion.div
                      variants={copyItemVariants}
                      className={
                        styles.roleGrid
                      }
                    >
                      {project.role.map(
                        (role, index) => (
                          <div key={role}>
                            <strong>
                              {String(
                                index + 1,
                              ).padStart(
                                2,
                                "0",
                              )}
                            </strong>

                            <p>{role}</p>
                          </div>
                        ),
                      )}
                    </motion.div>

                    <motion.p
                      variants={copyItemVariants}
                      className={
                        styles.techLine
                      }
                    >
                      {project.technologies.join(
                        " · ",
                      )}
                    </motion.p>
                  </motion.div>
                </section>

                {/* SOLUTION */}
                <section
                  className={styles.slide}
                  aria-hidden={
                    activeSlide !==
                    solutionSlideIndex
                  }
                >
                  <motion.div
                    className={
                      styles.readingColumn
                    }
                    initial={false}
                    variants={
                      reduceMotion
                        ? undefined
                        : copyContainerVariants
                    }
                    animate={
                      reduceMotion
                        ? undefined
                        : activeSlide === solutionSlideIndex
                          ? "visible"
                          : "hidden"
                    }
                  >
                    <motion.span variants={copyItemVariants}>
                      04 / SOLUTION
                    </motion.span>

                    <motion.h2 variants={copyItemVariants}>
                     문제를 해결한 기술과 구조
                    </motion.h2>

                    <motion.p variants={copyItemVariants}>
                      {project.solution}
                    </motion.p>
                  </motion.div>
                </section>

                {/* RESULT */}
                <section
                  className={styles.slide}
                  aria-hidden={
                    activeSlide !==
                    resultSlideIndex
                  }
                >
                  <motion.div
                    className={
                      styles.readingColumn
                    }
                    initial={false}
                    variants={
                      reduceMotion
                        ? undefined
                        : copyContainerVariants
                    }
                    animate={
                      reduceMotion
                        ? undefined
                        : activeSlide === resultSlideIndex
                          ? "visible"
                          : "hidden"
                    }
                  >
                    <motion.span variants={copyItemVariants}>
                      05 / RESULT
                    </motion.span>

                    <motion.h2 variants={copyItemVariants}>
                      구현 결과와 핵심 성과
                    </motion.h2>

                    <motion.div
                      variants={copyItemVariants}
                      className={
                        styles.metricGrid
                      }
                    >
                      {project.metrics.map(
                        (metric) => (
                          <div
                            key={
                              metric.label
                            }
                          >
                            <strong>
                              {
                                metric.value
                              }
                            </strong>

                            <p>
                              {
                                metric.label
                              }
                            </p>
                          </div>
                        ),
                      )}
                    </motion.div>
                  </motion.div>
                </section>

              {/* GALLERY 페이지 */}
{hasGallery && (
  <section
    className={`
      ${styles.slide}
      ${styles.gallerySlide}
    `}
    aria-hidden={
      activeSlide !== gallerySlideIndex
    }
  >
    <div className={styles.galleryContent}>
      <span>06 / GALLERY</span>

      <div className={styles.galleryVisual}>
        <ProjectImage
          ref={galleryRef}
          projectId={project.id}
          title={project.title}
          accent={project.accent}
          gallery
          onBoundarySwipe={(
            direction,
          ) => {
            if (direction === -1) {
              moveSlide(-1);
            }
          }}
        />
      </div>
    </div>
  </section>
)}
              </motion.div>
            </div>
          </div>

          <AnimatePresence>
            {shouldShowScrollHint && (
              <motion.div
                className={
                  styles.scrollHint
                }
                aria-hidden="true"
                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity: 0,
                        y: 10,
                      }
                }
                animate={
                  reduceMotion
                    ? {
                        opacity: 1,
                      }
                    : {
                        opacity: 1,
                        y: [
                          0,
                          -5,
                          0,
                        ],
                        scale: [
                          1,
                          1.018,
                          1,
                        ],
                      }
                }
                exit={
                  reduceMotion
                    ? {
                        opacity: 0,
                        transition: {
                          duration: 0,
                        },
                      }
                    : {
                        opacity: 0,
                        y: 8,
                        transition: {
                          duration: 0.25,
                          ease: [
                            0.22,
                            1,
                            0.36,
                            1,
                          ],
                        },
                      }
                }
                transition={
                  reduceMotion
                    ? {
                        duration: 0,
                      }
                    : {
                        opacity: {
                          duration: 0.4,
                        },

                        y: {
                          duration: 1.7,
                          repeat: Infinity,
                          ease: "easeInOut",
                        },

                        scale: {
                          duration: 1.7,
                          repeat: Infinity,
                          ease: "easeInOut",
                        },
                      }
                }
              >
                <span>←</span>

                <strong
                  className={
                    styles.desktopHintText
                  }
                >
                  SCROLL &amp; SWIPE
                </strong>

                <strong
                  className={
                    styles.mobileHintText
                  }
                >
                  SWIPE
                </strong>

                <span>→</span>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {hasGallery &&
              showGalleryOpenHint &&
              activeSlide ===
                gallerySlideIndex && (
                <motion.div
                  className={
                    styles.galleryOpenHint
                  }
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity: 0,
                          y: 8,
                        }
                  }
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                    y: 4,
                  }}
                  transition={{
                    duration: reduceMotion
                      ? 0
                      : 0.38,
                    ease: [
                      0.22,
                      1,
                      0.36,
                      1,
                    ],
                  }}
                >
                  <span aria-hidden="true">
                    ↗
                  </span>

                  <strong>
                    이미지를 누르면 상세
                    이미지로 볼 수 있습니다.
                  </strong>
                </motion.div>
              )}
          </AnimatePresence>

          <footer className={styles.footer}>
            <div
              className={styles.progress}
              aria-label={`${slideCount}단계 중 ${activeSlide + 1}단계`}
            >
              <span>
                {String(
                  activeSlide + 1,
                ).padStart(2, "0")}
              </span>

              <div>
                <i
                  style={{
                    transform: `scaleX(${
                      (activeSlide + 1) /
                      slideCount
                    })`,
                  }}
                />
              </div>

              <span>
                {String(
                  slideCount,
                ).padStart(2, "0")}
              </span>

              <strong>
                {currentSlideLabels[activeSlide]}
              </strong>
            </div>

            <div className={styles.arrows}>
              <button
                type="button"
                onClick={() => {
                  moveSlide(-1);
                }}
                disabled={
                  activeSlide === 0
                }
                aria-label="이전 슬라이드"
              >
                ←
              </button>

              <button
                type="button"
                onClick={() => {
                  moveSlide(1);
                }}
                disabled={
                  activeSlide ===
                  slideCount - 1
                }
                aria-label="다음 슬라이드"
              >
                →
              </button>
            </div>
          </footer>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
